import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function Footer() {
  const { language, switchLanguage, t } = useLanguage();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (id) => {
    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
  };

  const footerLinks = [
    { id: 'quem-somos', label: t.footer.quemSomos, color: '#6E2D60' },
    { id: 'produtos', label: t.footer.produtos, color: '#2C4A85' },
    { id: 'contato', label: t.footer.contato, color: '#6E2D60' },
    { id: 'privacidade', label: t.footer.privacidade, color: '#2D6B6B' },
  ];

  const languageLabel = language === 'pt' ? t.footer.portugues : t.footer.ingles;

  const openCookiePrefs = () => {
    window.dispatchEvent(new Event('openCookiePreferences'));
  };

  return (
    <footer
      data-testid="main-footer"
      className="bg-[#F9F8F6] py-16 px-8 md:px-16"
    >
      <div className="max-w-2xl mx-auto">
        {/* Top: Language selector + Links */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 mb-10">
          {/* Language Selector Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="footer-language-selector"
                className="flex items-center gap-2 text-sm font-light text-[#333333]/90 hover:text-[#35589B] transition-colors duration-300"
              >
                <span>{languageLabel}</span>
                <ChevronDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="bg-[#F9F8F6] border-[#333333]/10 min-w-[140px]"
            >
              <DropdownMenuItem
                data-testid="footer-lang-pt"
                onClick={() => switchLanguage('pt')}
                className={`text-sm font-light cursor-pointer ${
                  language === 'pt' ? 'text-[#35589B]' : 'text-[#333333]/90'
                }`}
              >
                Português
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="footer-lang-en"
                onClick={() => switchLanguage('en')}
                className={`text-sm font-light cursor-pointer ${
                  language === 'en' ? 'text-[#35589B]' : 'text-[#333333]/90'
                }`}
              >
                English
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Footer Links */}
          <nav className="flex flex-wrap items-center gap-6" data-testid="footer-nav">
            {footerLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`footer-link-${link.id}`}
                onClick={() => {
                  if (link.id === 'privacidade') {
                    navigate('/privacidade');
                  } else if (link.id === 'contato') {
                    navigate('/contato');
                  } else {
                    scrollTo(link.id);
                  }
                }}
                className="text-sm font-light hover:underline hover:opacity-80 transition-all duration-300"
                style={{ color: link.color }}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Separator */}
        <div className="w-full h-[1px] bg-[#333333]/10 mb-8" />

        {/* Bottom: CNPJ, Address, Cookie Prefs, Copyright */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-4 text-xs font-light text-[#555555]">
            <span data-testid="footer-cnpj">CNPJ: 50.895.412/0001-05</span>
            <span className="hidden sm:inline">|</span>
            <span data-testid="footer-address">Marabá - PA</span>
            <span className="hidden sm:inline">|</span>
            <button
              data-testid="footer-cookie-prefs"
              onClick={openCookiePrefs}
              className="text-[#2D6B6B] hover:underline transition-colors duration-300"
            >
              {t.footer.cookiePrefs}
            </button>
          </div>
          <p
            data-testid="footer-copyright"
            className="text-xs font-light text-[#4D4D4D]"
          >
            &copy; {currentYear} Wee Time LTDA
          </p>
        </div>
      </div>
    </footer>
  );
}
